/**
 * HR → «Грейды» (26.08, владелец): грейд 1..8 на пару оператор×квест.
 * Чипы операторов: тап — выбрать, смахнуть — скрыть из показа (localStorage,
 * личная настройка; жёлтый квадратик — дропдаун скрытых с «Восстановить всех»).
 * Строка квеста — ОДНОЙ строкой: квест · сыграно · кнопки 1..8 · ставка.
 * Выставление оптимистичное (мгновенно, откат при ошибке сервера). ОО ставит
 * 1..5, ЕО — любой (гейт и на сервере). Автоставки 1–6 → operator_rates;
 * ручная правка ставки офисами главнее.
 * 13.10 (ПР10 архива 1С, Р11): под строками квестов — свёрнутый блок «За всё время в ЮниКвесте» выбранного оператора.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import type { ApiList } from "../../api/types";
import type { BoardOperator } from "../../hooks/useOoDay";
import { NumPadPop } from "../../components/shared";
import { useChipSwipe } from "./useChipSwipe";
import { useClampPop } from "./useClampPop";
import { AllTimeBlock } from "../../components/allTime/AllTimeBlock";
import css from "./board.module.css";

const AUTO_RATE: Record<number, number> = { 1: 350, 2: 450, 3: 550, 4: 599, 5: 600, 6: 700 };
const REQ: Record<number, string> = {
  1: "новичок · автоставка ₽350",
  2: "10 игр на квесте + акцепты двух операторов (≥4 и ≥5 грейд) · ₽450",
  3: "20 игр + акцепты двух операторов ≥5 грейда · ₽550",
  4: "ещё 10 игр + акцепт оператора 6-го грейда · ₽599",
  5: "акцепт ОО · ₽600",
  6: "своя спецверсия + 200 игр на площадке · ₽700",
  7: "решение EO",
  8: "решение EO",
};
const HIDE_LS = "ub_grades_hidden";

interface QuestRow { id: string; name: string; short_name?: string; branch_id?: string; active?: number }

export function GradesTab({ operators }: { operators: BoardOperator[] }) {
  const maxGrade = 8; /* 26.08 v2 (владелец): ОО и ЕО выставляют все грейды */
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { const v = JSON.parse(localStorage.getItem(HIDE_LS) || "[]") as unknown; return new Set(Array.isArray(v) ? v.map(String) : []); } catch { return new Set(); }
  });
  const saveHidden = (n: Set<string>) => { try { n.size ? localStorage.setItem(HIDE_LS, JSON.stringify([...n])) : localStorage.removeItem(HIDE_LS); } catch { /* private mode */ } };
  const [sel, setSel] = useState<string>("");
  const shown = operators.filter((o) => !hidden.has(o.id));
  useEffect(() => { if (!sel && shown[0]) setSel(shown[0].id); }, [sel, shown]);
  const hideOp = (id: string) => setHidden((s) => { const n = new Set(s); n.add(id); saveHidden(n); if (sel === id) setSel(""); return n; });
  const unhide = (id: string) => setHidden((s) => { const n = new Set(s); n.delete(id); saveHidden(n); return n; });
  const { swDown, swMove, swUp, swMoved } = useChipSwipe(hideOp);
  const [resetOpen, setResetOpen] = useState(false);
  const resetRef = useClampPop<HTMLDivElement>(resetOpen || null);
  useEffect(() => { if (!resetOpen) return; const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.addOpWrap}`)) setResetOpen(false); }; document.addEventListener("pointerdown", onDown, true); return () => document.removeEventListener("pointerdown", onDown, true); }, [resetOpen]);

  const [quests, setQuests] = useState<QuestRow[]>([]);
  const [grades, setGrades] = useState<Record<string, number>>({});
  const [played, setPlayed] = useState<Record<string, number>>({});
  const [rates, setRates] = useState<Record<string, number>>({});
  const [err, setErr] = useState<string | null>(null);
  /* ставка редактируется NumPadPop и пишется в operator_rates (26.08 v2, владелец) */
  const [rateFor, setRateFor] = useState<string | null>(null);
  const rateAnchor = useRef<HTMLElement | null>(null);
  const selOp = operators.find((o) => o.id === sel);
  const saveRate = (questId: string, v: number) => {
    if (v <= 0) return;
    const prev = rates[questId];
    setRates((x) => ({ ...x, [questId]: v }));
    api("/api/rates/operators", { method: "PUT", body: { operator_id: sel, rates: { [questId]: v } } })
      .catch(() => setRates((x) => { const n = { ...x }; if (prev == null) delete n[questId]; else n[questId] = prev; return n; }));
  };

  useEffect(() => {
    let alive = true;
    api<ApiList<QuestRow>>("/api/quests").then((r) => { if (alive) setQuests((r.results ?? []).filter((q) => q.active !== 0)); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!sel) return;
    let alive = true;
    setGrades({}); setPlayed({});
    api<ApiList<{ quest_id: string; grade: number }>>(`/api/grades?operator_id=${sel}`)
      .then((r) => { if (alive) setGrades(Object.fromEntries((r.results ?? []).map((g) => [g.quest_id, g.grade]))); }).catch(() => {});
    api<ApiList<{ quest_id: string; n: number }>>(`/api/grades/played?operator_id=${sel}`)
      .then((r) => { if (alive) setPlayed(Object.fromEntries((r.results ?? []).map((p) => [p.quest_id, Number(p.n)]))); }).catch(() => {});
    setRates({ ...(selOp?.rates ?? {}) });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);

  const rows = useMemo(() => [...quests].sort((a, b) => (a.branch_id ?? "").localeCompare(b.branch_id ?? "") || a.name.localeCompare(b.name, "ru")), [quests]);

  /* оптимистично: грейд и автоставка встают МГНОВЕННО, откат при ошибке сервера */
  const setGrade = (questId: string, grade: number) => {
    if (grade > maxGrade) return;
    const prevG = grades[questId]; const prevR = rates[questId];
    setErr(null);
    setGrades((g) => ({ ...g, [questId]: grade }));
    if (AUTO_RATE[grade] != null) setRates((x) => ({ ...x, [questId]: AUTO_RATE[grade]! }));
    api("/api/grades", { method: "PUT", body: { operator_id: sel, quest_id: questId, grade } })
      .catch((e: unknown) => {
        setErr(e instanceof Error ? e.message : "Не удалось");
        setGrades((g) => { const n = { ...g }; if (prevG == null) delete n[questId]; else n[questId] = prevG; return n; });
        setRates((x) => { const n = { ...x }; if (prevR == null) delete n[questId]; else n[questId] = prevR; return n; });
      });
  };

  return (
    <div className={css.content}>
      <div className={css.schedChips}>
        {shown.map((o) => (
          <button key={o.id} type="button" className={`${css.schedChip} ${sel === o.id ? css.on : ""}`}
            title={`${o.name}; смахнуть — скрыть из показа`}
            onPointerDown={(e) => swDown(o.id, e)} onPointerMove={swMove}
            onPointerUp={swUp} onPointerCancel={swUp} onContextMenu={(e) => e.preventDefault()}
            onClick={() => { if (swMoved.current) { swMoved.current = false; return; } setSel(o.id); }}
          >{o.name}</button>
        ))}
        {hidden.size ? (
          <span className={css.addOpWrap}>
            <button type="button" className={css.schedReset} title={`Скрытые операторы: ${hidden.size}`} onClick={() => setResetOpen((v) => !v)} />
            {resetOpen ? (
              <div className={css.mkOplist} ref={resetRef}>
                {operators.filter((o) => hidden.has(o.id)).map((o) => (
                  <button key={o.id} type="button" className={css.opItem} onClick={() => unhide(o.id)}><span className={css.opName}>{o.name}</span></button>
                ))}
                <button type="button" className={css.opItem} onClick={() => { setHidden(new Set()); saveHidden(new Set()); setResetOpen(false); }}><span className={css.opName}><b>Восстановить всех</b></span></button>
              </div>
            ) : null}
          </span>
        ) : null}
      </div>
      {err ? <div className={css.actHint}>{err}</div> : null}
      {!sel ? <div className={css.empty}>Выбери оператора</div> : rows.map((q) => {
        const g = grades[q.id] ?? 0;
        const rate = rates[q.id];
        return (
          <div key={q.id} className={css.grRow}>
            <span className={css.grQuest} title={`${q.name}${q.branch_id ? ` · ${q.branch_id}` : ""}`}>{q.short_name || q.name}</span>
            <span className={css.grPlayed} title="Сыграно игр на квесте">{played[q.id] ?? 0}и</span>
            <span className={css.grBtns}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((k) => (
                <button key={k} type="button"
                  className={`${css.grBtn} ${g === k ? css.on : ""} ${k > maxGrade ? css.grLock : ""}`}
                  title={`${REQ[k] ?? ""}${k > maxGrade ? " · только EO" : ""}`}
                  disabled={k > maxGrade}
                  onClick={() => setGrade(q.id, k)}
                >{k}</button>
              ))}
            </span>
            <button type="button" className={css.grRate} title="Тап — изменить ставку (запишется в таблицу ставок)"
              onClick={(e) => { rateAnchor.current = e.currentTarget; setRateFor(q.id); }}>
              {rate != null ? `₽${rate}` : "₽350"}
            </button>
          </div>
        );
      })}
      {sel ? <AllTimeBlock key={sel} operatorId={sel} spaced /> : null}
      <NumPadPop
        open={!!rateFor} anchor={rateAnchor.current} label={`Ставка ₽ — ${selOp?.name ?? ""}`}
        mode="money" value={rateFor ? rates[rateFor] ?? 350 : 350} max={99999}
        onCommit={(v) => { if (rateFor) saveRate(rateFor, v); }}
        onClose={() => setRateFor(null)}
      />
    </div>
  );
}
